'use client';

/** @see `frontend/docs/faq-client-fetch-policy.md` */

import { buttonVariants, headerVariants, inputFocusAnimation, inputHoverAnimation, pageVariants } from "@/lib/animations";
import { motion } from "framer-motion";
import Link from "next/link";
import React, { useEffect, useState, useMemo } from "react";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { FaqCategoryNode } from "@/types/faq";
import type { PageUrlMap } from "@/types/blog";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import { debugLog, debugWarn, logError } from "@/lib/utils/debug";
import FaqSearchDropdown from "@/components/ui/FaqSearchDropdown";

export interface FaqPopularCategory {
    name: string;
    href: string;
}

interface FaqsPageContentProps {
    popularCategories?: FaqPopularCategory[];
    language?: string;
    languageCode?: string;
    languageId?: number;
    /**
     * Başlık (GrapeJS editöründen düzenlenebilir)
     */
    title?: string;
    /**
     * Açıklama (GrapeJS editöründen düzenlenebilir)
     */
    description?: string;
    /**
     * Page URLs map (context'ten gelir)
     */
    pageUrls?: PageUrlMap;
    /**
     * Default language (context'ten gelir)
     */
    defaultLanguage?: string;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    __pageContext?: {
        language?: string;
        languageCode?: string;
        languageId?: number;
        pageUrls?: PageUrlMap;
        defaultLanguage?: string;
    };
}

export default function FaqsPageContent({ 
    popularCategories: initialPopularCategories,
    language,
    languageCode,
    languageId,
    title = 'Hello, what can we help you find?',
    description = 'Docly is a fully featured knowledge base theme for WordPress.',
    pageUrls,
    defaultLanguage = 'tr',
    enableClientFetch = false,
    __pageContext
}: FaqsPageContentProps){
    const [categories, setCategories] = useState<FaqPopularCategory[]>(initialPopularCategories || []);
    const [loading, setLoading] = useState(false);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});
    
    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    // Memoize resolved values to prevent unnecessary re-renders
    const resolvedLanguageCode = useMemo(
        () =>
            language ||
            languageCode ||
            __pageContext?.language ||
            __pageContext?.languageCode ||
            undefined,
        [language, languageCode, __pageContext?.language, __pageContext?.languageCode]
    );

    const resolvedLanguageId = useMemo(
        () => languageId || __pageContext?.languageId || undefined,
        [languageId, __pageContext?.languageId]
    );

    // Page URLs ve default language'i context'ten al
    const resolvedPageUrls = useMemo(
        () => pageUrls || __pageContext?.pageUrls,
        [pageUrls, __pageContext?.pageUrls]
    );
    const resolvedDefaultLanguage = useMemo(() => {
        const lang = defaultLanguage || __pageContext?.defaultLanguage;
        if (!lang) {
            if (process.env.NODE_ENV === 'development') {
                console.error('FaqsPageContent: defaultLanguage prop is required');
            }
            // Production'da boş string döndür (hata fırlatma)
            return '';
        }
        return lang;
    }, [defaultLanguage, __pageContext?.defaultLanguage]);

    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = initialPopularCategories && initialPopularCategories.length > 0;
    // Eğer initialPopularCategories'teki link'ler # ile başlıyorsa, 
    // API'den çekip pageUrls'den link'leri oluşturmalıyız (pageUrls olsun ya da olmasın)
    const hasHashLinks = hasInitialData && initialPopularCategories?.some(cat => cat.href.startsWith('#'));
    const shouldFetch = (enableClientFetch && !hasInitialData) || hasHashLinks;
    

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // FAQ kategorilerini client-side fetch
    useEffect(() => {
        // Eğer hash link'ler varsa, her zaman API'den çekmeliyiz
        if (!hasHashLinks && !shouldFetch) {
            // Hash link yoksa ve fetch yapılmayacaksa, initialPopularCategories'i kullan
            if (hasInitialData) {
                setCategories(initialPopularCategories || []);
            }
            return;
        }
        
        // hasHashLinks true ise veya shouldFetch true ise, API'den çek

        const abortController = new AbortController();
        const fetchCategories = async () => {
            try {
                setLoading(true);

                // API parametrelerini oluştur
                const params = new URLSearchParams();
                
                // Dil parametresini belirle
                let finalLanguage: string | undefined = undefined;
                
                if (resolvedLanguageCode) {
                    finalLanguage = resolvedLanguageCode;
                } else if (resolvedLanguageId) {
                    params.set('language_id', String(resolvedLanguageId));
                } else if (typeof window !== 'undefined') {
                    const pathParts = window.location.pathname.split('/').filter(Boolean);
                    const possibleLang = pathParts[0];
                    if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
                        finalLanguage = possibleLang;
                    } else {
                        const cookieLang = document.cookie
                            .split('; ')
                            .find(row => row.startsWith('language='))
                            ?.split('=')[1];
                        finalLanguage = cookieLang || undefined;
                    }
                }

                if (finalLanguage) {
                    params.set('language', finalLanguage);
                }

                const response = await fetch(`/api/faqs?${params.toString()}`, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                const data = await response.json();
                const faqData = data?.data || {};
                const categoriesData: FaqCategoryNode[] = faqData?.categories || [];
                
                // API response'undan pageUrls ve defaultLanguage al (varsa)
                const apiPageUrls = (data?.data?.pageUrls as PageUrlMap | undefined) || undefined;
                const apiDefaultLanguage = (data?.data?.default_language as string | undefined) || resolvedDefaultLanguage;

                // Kategorileri FaqPopularCategory formatına dönüştür
                // Sadece FAQ'leri olan kategorileri al ve ilk 4-5'ini seç
                const currentLanguage = finalLanguage || resolvedLanguageCode || resolvedDefaultLanguage;
                
                // pageUrls öncelik sırası: API response > props/context > fallback
                const effectivePageUrls = apiPageUrls || resolvedPageUrls;
                const effectiveDefaultLanguage = apiDefaultLanguage || resolvedDefaultLanguage;
                
                // pageUrls varsa ondan basePath'i al
                if (!effectiveDefaultLanguage) {
                    console.error('FaqsPageContent: defaultLanguage is required');
                    return;
                }
                const currentLang = currentLanguage || effectiveDefaultLanguage;
                const defaultLang = effectiveDefaultLanguage;
                // Popüler konular: kategori sayfası faq-cat URL'i (FaqSearchDropdown / FaqCatPage ile uyumlu)
                const faqsBaseFallback = effectivePageUrls
                    ? resolveUrlFromMap(
                        effectivePageUrls,
                        'faqs',
                        currentLang,
                        defaultLang,
                        '/faqs'
                    )
                    : '/faqs';
                const basePath = effectivePageUrls
                    ? resolveUrlFromMap(
                        effectivePageUrls,
                        'faq-cat',
                        currentLang,
                        defaultLang,
                        faqsBaseFallback
                    )
                    : faqsBaseFallback;

                const popularCategories: FaqPopularCategory[] = categoriesData
                    .filter((category) => category.faqs && category.faqs.length > 0)
                    .slice(0, 5) // İlk 5 kategori
                    .map((category) => {
                        const slug = category.slug || String(category.id);
                        // pageUrls varsa buildLocalizedLink kullan, yoksa basit path oluştur
                        const href = effectivePageUrls
                            ? buildLocalizedLink(basePath, slug, defaultLang)
                            : `${basePath}/${slug}`;
                        
                        return {
                            name: category.name,
                            href,
                        };
                    });

                setCategories(popularCategories);
            } catch (err) {
                if (abortController.signal.aborted) {
                    return;
                }
                logError('[FaqsPageContent] Categories fetch error:', err);
                // Hata durumunda boş array kullan (fallback kategoriler render'da gösterilecek)
                setCategories([]);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchCategories();
        return () => abortController.abort();
    }, [shouldFetch, hasHashLinks, resolvedLanguageCode, resolvedLanguageId, hasInitialData, initialPopularCategories, resolvedPageUrls, resolvedDefaultLanguage]);

    // Kategorileri render için hazırla - useMemo ile optimize et
    const categoriesToRender = useMemo(() => {
        if (categories.length > 0) {
            return categories;
        }
        // Fallback kategoriler sadece loading false ve veri yoksa gösterilir
        // Hardcoded href yerine boş array döndür (kullanıcı API'den kategorileri seçmeli)
        return [];
    }, [categories]);
    return (
        <motion.div
        className="bg-[#F4F6F8] pt-[75px] pb-[60px]"
        initial="hidden"
        animate="visible"
        variants={pageVariants}
    >
        <div className="container mx-auto">
            <div className="flex flex-col gap-5">
                <motion.div
                    className="flex flex-col gap-6 items-center"
                    variants={headerVariants}
                >
                    <motion.h1
                        className="font-bold text-alsero-dark-gray text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight text-center"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ type: "spring" as const, stiffness: 100, damping: 15 }}
                        data-prop-path="title"
                    >
                        {title || "Title alanı"}
                    </motion.h1>
                    <motion.p
                        className="text-alsero-light-gray text-sm sm:text-base md:text-lg font-medium leading-relaxed text-center max-w-2xl mx-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        data-prop-path="description"
                    >
                        {description || "Description alanı"}
                    </motion.p>
                </motion.div>
                <motion.div
                    className="lg:w-8/12 mx-auto mt-11"
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                        type: "spring" as const,
                        stiffness: 100,
                        damping: 15,
                        delay: 0.3
                    }}
                >
                    <FaqSearchDropdown
                        language={resolvedLanguageCode || resolvedDefaultLanguage}
                        defaultLanguage={resolvedDefaultLanguage}
                        pageUrls={resolvedPageUrls}
                        translations={variables}
                    />
                </motion.div>
                <div className="mx-auto">
                    <div className="flex flex-row gap-6 items-center">
                        <h3 className="font-semibold text-alsero-dark-gray text-lg">{getVariable('faqs_page_popular_topics', 'Popular Topics')}</h3>
                        <ul className="grid grid-cols-12 gap-3">
                            {loading ? (
                                <li className="col-span-4 text-center text-gray-500">Kategoriler yükleniyor...</li>
                            ) : (
                                categoriesToRender.slice(0, 5).map((cat, index) => (
                                <motion.li
                                    key={`popular_${index}`}
                                    className="col-span-4 group px-5 py-4 bg-white transition-colors ease-in-out duration-300 hover:bg-alsero-first border border-[#EDEDED] hover:border-alsero-first"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Link href={cat.href} className="text-center text-alsero-dark-gray font-medium text-[15px] group-hover:text-white">
                                        <h2>{cat.name}</h2>
                                    </Link>
                                </motion.li>
                                ))
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
    )
}